import {observer} from 'mobx-react';
import React, {Dispatch, SetStateAction, useEffect, useState} from 'react';
import {Spin} from 'antd';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import {useStore} from '@/store/root-store';
import {openUrl} from '@/utils/router';
import {LoadingOutlined} from '@ant-design/icons';
import {copyToClipBoard} from '@/utils/clipboard';
import {faEnvelope} from '@fortawesome/pro-light-svg-icons';
import {Button} from '@/components/common-components/v2/Button';
import {faCircleCheck, faCircleExclamation} from '@fortawesome/pro-solid-svg-icons';
import FreezeWrapper from '../../otto-page-detail/Component/freezTooltip';
import {addProtocolToDomainHttps} from '@/utils/url';
import {getSaUrl} from '@/api/gsc/utils';
import {WordPressIntegrationWrapper} from '../Component/settings-modal';
import styles from '../styles.module.scss';

interface Props {
  widgetStatus: number;
  setWidgetStatus: Dispatch<SetStateAction<number>>;
  existSiteAudit: string;
  inputVal: string;
  inputValue: string;
  detectedCms?: string | null;
}
export const StepThree: React.FC<Props> = observer((
  {
    widgetStatus,
    setWidgetStatus,
    existSiteAudit,
    inputVal,
    inputValue,
    detectedCms,
  }) => {
  const {ottoV2Store: {
    checkScriptFromUrl,
    scriptStatusLoading,
    ottoV2Project,
  }, settings: {customer: {profile: {whitelabelOtto, isWhitelabel}}}} = useStore('');

  const [activeVideoTab, setActiveVideoTab] = useState<string>('word-press');
  const [iframeLoading, setIframeLoading] = useState(true);

  const videos = {
    'word-press': 'https://www.loom.com/embed/4369bc50fb3e4a6e8c6f17f307f5691e?sid=1e959034-021d-4f53-affd-a84e2a46c884',
    'shopify': 'https://www.loom.com/embed/5481d00cd9fb4240a48ec4ea429b6c51?sid=7d1dcabf-4ab8-4217-9082-c98451bee694',
    'webflow': 'https://www.loom.com/embed/a3bdaa56936d4b44842a4bf1924ba429?sid=d7e635fe-ad8b-4614-92e1-bd586f046ab5',
    'wix': 'https://www.loom.com/embed/b991de7519fe4f1ba391cccfd1b39c5a?sid=dd29cac3-b10a-4b3a-9dc5-76fa4c0547ef',
    'squarespace': 'https://www.loom.com/embed/459ce062b9ea4318aea9c9f3aebfe9a0?sid=048cd83c-b2ed-4357-91db-ec297720342b',
    'clickfunnels': 'https://www.loom.com/embed/c52dcb4216624601a1f0eaea074cea49?sid=c061e699-d3c7-4457-a6e3-238d633c878a',
    'gtm': 'https://www.loom.com/embed/cf0e531e520f436c8253b1c9c2e4c855?sid=0f88d569-3cf1-4a93-9034-128f9d121454',
    'custom': `${getSaUrl()}/wp-content/uploads/2024/06/squarespace.mp4`,
  };

  const handleTabChange = key => {
    setIframeLoading(true);
    setActiveVideoTab(key);
  };

  const cmsMapping = {
    'wordpress': 'word-press',
  };

  useEffect(() => {
    const mappedCms = cmsMapping[detectedCms] || detectedCms || 'word-press';
    setActiveVideoTab(mappedCms);
  }, [detectedCms]);

  const checkScript = async () => {
    let val = '';
    if (existSiteAudit === 'new-site') {
      val = inputVal;
    } else {
      val = inputValue;
    }
    if (!val) {
      val = ottoV2Project?.hostname;
    }
    const response = await checkScriptFromUrl(addProtocolToDomainHttps(val));
    if (response?.pixelState === 'installed' || response?.pixelState === 'wp_plugin') {
      setWidgetStatus(1);
    } else if (response?.pixelState === 'not_installed' || !response?.pixelState) {
      setWidgetStatus(2);
    } else if (response?.pixelState === 'wrong_uuid' || response?.pixelState === 'wrong_domain') {
      setWidgetStatus(3);
    }
  };

  const wordpressIntegration = () => {
    return (
      <WordPressIntegrationWrapper>
        <div className='title'>Install our WordPress plugin at: <a href='https://wordpress.org/plugins/metasync/' target='_blank' rel='noopener noreferrer'>https://wordpress.org/plugins/metasync/</a></div>
        <div>After installing our plugin, at wordpress follow these steps:</div>
        <ol>
          <li>Click the{!isWhitelabel ? <span> SearchAtlas</span> : null} logo in the left-hand menu.</li>
          <li>Navigate to <span>General Settings</span>.</li>
          <li>Check the <span>Enable {whitelabelOtto}</span> option.</li>
          <li>Paste the {whitelabelOtto} UUID: <span className='uuid'>{ottoV2Project?.uuid}</span></li>
        </ol>
      </WordPressIntegrationWrapper>
    );
  };

  return ( <>
    {!isWhitelabel ? <VideosWrapper id='otto-install-video'>
      <VideoChanger className={styles.videoChanger}>
        Platform:
        <div
          className={`button ${activeVideoTab === 'word-press' ? 'active' : ''}`}
          onClick={() => handleTabChange('word-press')}
        >WordPress</div>
        <div
          className={`button ${activeVideoTab === 'shopify' ? 'active' : ''}`}
          onClick={() => handleTabChange('shopify')}
        >Shopify</div>
        <div
          className={`button ${activeVideoTab === 'webflow' ? 'active' : ''}`}
          onClick={() => handleTabChange('webflow')}
        >Webflow</div>
        <div
          className={`button ${activeVideoTab === 'wix' ? 'active' : ''}`}
          onClick={() => handleTabChange('wix')}
        >Wix</div>
        <div
          className={`button ${activeVideoTab === 'squarespace' ? 'active' : ''}`}
          onClick={() => handleTabChange('squarespace')}
        >Squarespace</div>
        <div
          className={`button ${activeVideoTab === 'clickfunnels'? 'active' : ''}`}
          onClick={() => handleTabChange('clickfunnels')}
        >Clickfunnels</div>
        <div
          className={`button ${activeVideoTab === 'gtm'? 'active' : ''}`}
          onClick={() => handleTabChange('gtm')}
        >GTM</div>
        <div
          className={`button ${activeVideoTab === 'custom' ? 'active' : ''}`}
          onClick={() => handleTabChange('custom')}
        >Custom</div>
      </VideoChanger>
      <div style={{width: '100%', height: '200px', position: 'relative'}}>
        {iframeLoading && (
          <div style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#f2f2f5', borderRadius: '0 0 12px 12px'}}>
            <Spin indicator={<LoadingOutlined style={{fontSize: 24}} spin />} />
          </div>
        )}
        {activeVideoTab === 'custom' ? (
          <video
            autoPlay={false}
            controls
            style={{borderRadius: '0 0 12px 12px', width: '100%', height: '100%'}}
            muted
            src={videos[activeVideoTab]}
            onLoadedData={() => setIframeLoading(false)}
          />
        ) : (
          <iframe
            style={{borderRadius: '0 0 12px 12px', width: '100%', height: '100%'}}
            frameBorder='0'
            allowFullScreen
            src={videos[activeVideoTab]}
            onLoad={() => setIframeLoading(false)}
          />
        )}
      </div>
    </VideosWrapper> : ''}
    <div className={'inputs-wrapper'} style={{marginTop: '16px'}}>
      <div className='label-wrapper'>
        <div className={'label'}>Embed widget on your side</div>
        {!isWhitelabel ? <div className='heading' onClick={() => openUrl(`${getSaUrl()}/otto-pixel/`, '_blank')}>Installation guide</div> : ''}
      </div>
      <div className={'snippet-wrapper'}>
        <div style={{marginBottom: '10px', overflowWrap: 'break-word', fontSize: 12}}>
          {activeVideoTab === 'gtm' ? ottoV2Project?.pixelHtmlGtm : activeVideoTab === 'word-press' ? wordpressIntegration() :ottoV2Project?.pixelHtml}
        </div>
        <div className={'snippet-buttons'}>
          <Button background='#121212' textColor='#FFFFFF' onClick={()=>copyToClipBoard(activeVideoTab === 'gtm' ? ottoV2Project?.pixelHtmlGtm : activeVideoTab === 'word-press' ? ottoV2Project?.uuid : ottoV2Project?.pixelHtml)}>Copy {activeVideoTab === 'word-press' ? 'UUID' : 'code'} </Button>
          <FreezeWrapper>
            <a
              className={'email-button'}
              href={`mailto:?subject=Install this Widget on my site&body=Installation guide: ${getSaUrl()}/otto-pixel/ \n${activeVideoTab === 'gtm' ? ottoV2Project?.pixelHtmlGtm : activeVideoTab === 'word-press' ? ottoV2Project?.uuid :ottoV2Project?.pixelHtml}`}
            >
              <FontAwesomeIcon color={'#2D6CCA'} fontSize={16} icon={faEnvelope} />Email my developer
            </a>
          </FreezeWrapper>
        </div>
      </div>
    </div>
    {
      widgetStatus == -1 && <div className={'already-embed'} style={{border: 'none'}}>
        <div className={'text'}>Already embed the widget?&nbsp;<span onClick={checkScript} className={'link'}
        >Check widget status</span>{scriptStatusLoading && <Spin indicator={<LoadingOutlined style={{fontSize: 12, marginLeft: 10, display: 'flex', alignItems: 'center'}} spin />} />}</div>
      </div>
    }
    {
      widgetStatus === 1 && <div className={'embed-successfully'}>
        <FontAwesomeIcon icon={faCircleCheck} fontSize={16} color='#2AC155'/>
        <div className={'text'}>{`${whitelabelOtto} SEO Pixel was Installed Correctly.`}</div>
      </div>
    }
    {widgetStatus === 3 && <Banner>
      <FontAwesomeIcon icon={faCircleExclamation} fontSize={16} color='#F44343'/>
      <div className='banner-text'>{`${whitelabelOtto} SEO Pixel was not installed correctly. Copy & Paste the script to your website again.`}
        <span onClick={checkScript}>Check status</span>{scriptStatusLoading && <Spin indicator={<LoadingOutlined style={{fontSize: 12, marginLeft: 10, display: 'flex', alignItems: 'center'}} spin />} />}</div>
    </Banner>}
    {
      widgetStatus === 2 && <div className={'not-embed'}>
        <FontAwesomeIcon icon={faCircleExclamation} fontSize={16} color='#F44343'/>
        <div className={'text'}>{`${whitelabelOtto} SEO Pixel not Installed.`}
          <div className='check-again-button' onClick={checkScript}>Check again</div>{scriptStatusLoading && <Spin indicator={<LoadingOutlined style={{fontSize: 12, marginLeft: 10, display: 'flex', alignItems: 'center'}} spin />} />}</div>
      </div>
    }
  </>);
});

const VideosWrapper = styled.div`
  // height: 240px;
  position: relative;
`;
const VideoChanger = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #E8E8E8;
  background-color: rgba(0, 0, 0, 0.6);
  padding: 6px 14px;
  width: 100%;
  border-radius: 12px 12px 0px 0px;
  z-index: 1;
  .button {
    padding: 4px 7px;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: #E8E8E8;
    cursor: pointer;
    &.active {
      font-weight: 600;
      background-color: rgba(255, 255, 255, 0.35);
      border-radius: 6px;
      transition: background-color 0.15s ease-in-out, font-weight 0.15s ease-in-out;
    }
  }
`;
const Banner = styled.div`
  padding: 11px 15px;
  border-radius: 8px;
  background-color: rgba(244, 67, 67, 0.15);
  display: flex;
  align-items: center;
  gap: 6px;

  .banner-text {
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 16.94px;
    text-align: left;

    span {
      color: #2D6CCA;
      cursor: pointer;
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;
